import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useMemo } from 'react';

export interface StoreAnalytics {
  id: string;
  florist_id: string;
  date: string;
  total_orders: number;
  total_revenue: number;
  total_products: number;
  average_order_value: number;
  completed_orders: number;
  cancelled_orders: number;
  new_customers: number;
  popular_products: Array<{
    id: string;
    title: string;
    order_count: number;
    total_quantity: number;
  }>;
  created_at: string;
  updated_at: string;
}

interface UseStoreAnalyticsProps {
  startDate?: Date;
  endDate?: Date;
}

export function useStoreAnalytics({ startDate, endDate }: UseStoreAnalyticsProps = {}) {
  const { user } = useAuth();

  console.log("useStoreAnalytics - Current user:", user);
  const userId = useMemo(() => user?.id, [user?.id]);
  console.log("useStoreAnalytics - Using userId:", userId);

  const startDateString = useMemo(() => (startDate ? startDate.toISOString().split("T")[0] : undefined), [startDate]);
  const endDateString = useMemo(() => (endDate ? endDate.toISOString().split("T")[0] : undefined), [endDate]);
  console.log("useStoreAnalytics - Date range:", { startDateString, endDateString });

  const { data: analytics, isLoading, error } = useQuery({
    queryKey: ["storeAnalytics", userId, startDateString, endDateString],
    queryFn: async () => {
      if (!userId) throw new Error("User ID is required");
      console.log("useStoreAnalytics - Fetching data for user:", userId);

      let query = supabase
        .from("store_analytics")
        .select("*")
        .eq("florist_id", userId)
        .order("date", { ascending: false });

      if (startDateString) {
        query = query.gte("date", startDateString);
      }

      if (endDateString) {
        query = query.lte("date", endDateString);
      }

      const { data, error } = await query;

      if (error) {
        console.error("useStoreAnalytics - Error fetching analytics:", error);
        throw error;
      }
      
      console.log("useStoreAnalytics - Received data:", data);
      return data as StoreAnalytics[];
    },
    enabled: !!userId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchInterval: false
  });

  // Calculate period totals and trends
  const periodStats = analytics?.reduce(
    (acc, day) => {
      acc.totalRevenue += day.total_revenue;
      acc.totalOrders += day.total_orders;
      acc.totalProducts = day.total_products; // Use most recent count
      acc.completedOrders += day.completed_orders;
      acc.cancelledOrders += day.cancelled_orders;
      acc.newCustomers += day.new_customers;
      return acc;
    },
    {
      totalRevenue: 0,
      totalOrders: 0,
      totalProducts: 0,
      completedOrders: 0,
      cancelledOrders: 0,
      newCustomers: 0,
    }
  );

  console.log("useStoreAnalytics - Calculated stats:", periodStats);

  // Get today's analytics
  const today = analytics?.[0];

  // Get popular products for the period
  const popularProducts = analytics?.reduce((acc, day) => {
    day.popular_products.forEach((product) => {
      const existing = acc.find((p) => p.id === product.id);
      if (existing) {
        existing.order_count += product.order_count;
        existing.total_quantity += product.total_quantity;
      } else {
        acc.push({ ...product });
      }
    });
    return acc.sort((a, b) => b.total_quantity - a.total_quantity).slice(0, 5);
  }, [] as StoreAnalytics["popular_products"]);

  return {
    analytics,
    isLoading,
    error,
    today,
    periodStats,
    popularProducts,
  };
} 